import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Country, State, City } from 'country-state-city';
import apiClient from '../lib/apiClient';
import { getPhoneRequirementMessage, normalizePhoneForCountry, validatePhoneForCountry } from '../utils/phoneValidation';

const Contact = () => {
    const { t } = useLanguage();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        phoneCountryCode: 'IN',
        country: '',
        state: '',
        city: '',
        company: '',
        message: '',
    });

    const [status, setStatus] = useState('');
    const [countryOptions, setCountryOptions] = useState([]);
    const [stateOptions, setStateOptions] = useState([]);
    const [cityOptions, setCityOptions] = useState([]);

    useEffect(() => {
        setCountryOptions(Country.getAllCountries());
    }, []);

    const handleCountrySelect = (isoCode) => {
        const states = isoCode ? State.getStatesOfCountry(isoCode) : [];
        setStateOptions(states);
        setCityOptions([]);
        setFormData((prev) => ({
            ...prev,
            country: isoCode,
            state: '',
            city: '',
        }));
    };

    const handleStateSelect = (stateIso, countryIso) => {
        const baseCountry = countryIso || formData.country;
        const cities = stateIso && baseCountry
            ? City.getCitiesOfState(baseCountry, stateIso)
            : [];
        setCityOptions(cities);
        setFormData((prev) => ({
            ...prev,
            state: stateIso,
            city: '',
        }));
    };

    const handlePhoneCountryCodeChange = (isoCode) => {
        setFormData((prev) => ({
            ...prev,
            phoneCountryCode: isoCode,
        }));
    };

    const handleCitySelect = (cityName) => {
        setFormData((prev) => ({
            ...prev,
            city: cityName,
        }));
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus('');

        if (!formData.name || !formData.email || !formData.message) {
            setStatus(t('contact.form.fillRequired'));
            return;
        }

        const normalizedPhone = normalizePhoneForCountry(
            formData.phone,
            formData.phoneCountryCode || 'IN',
            countryOptions
        );
        if (formData.phone.trim()) {
            const phoneValidation = validatePhoneForCountry(formData.phone, formData.phoneCountryCode || 'IN', countryOptions);
            if (!phoneValidation.isValid) {
                setStatus(phoneValidation.message);
                return;
            }
        }

        setStatus('sending');

        try {
            await apiClient.post('/api/contact', {
                ...formData,
                phone: normalizedPhone || formData.phone,
            });

            setStatus('success');
            setFormData({
                name: '',
                email: '',
                phone: '',
                phoneCountryCode: 'IN',
                country: '',
                state: '',
                city: '',
                company: '',
                message: '',
            });
            setStateOptions([]);
            setCityOptions([]);

            // Clear success message after 5 seconds
            setTimeout(() => setStatus(''), 5000);
        } catch (error) {
            console.error('Contact form error:', error);
            setStatus(
                error?.response?.data?.error
                || error?.response?.data?.detail
                || error?.message
                || t('contact.form.error')
            );
        }
    };
    const styles = {
        section: {
            padding: '100px 32px',
            backgroundColor: '#F9F9F9',
        },
        container: {
            maxWidth: '1200px',
            margin: '0 auto',
        },
        header: {
            textAlign: 'center',
            marginBottom: '60px',
        },
        h2: {
            fontSize: '48px',
            fontWeight: '800',
            marginBottom: '16px',
            color: '#2B2B2B',
        },
        highlight: {
            color: '#3457D5',
        },
        subtext: {
            fontSize: '20px',
            color: '#6E6E6E',
            lineHeight: '1.7',
        },
        grid: {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '48px',
        },
        infoSection: {
            display: 'flex',
            flexDirection: 'column',
            gap: '32px',
        },
        infoCard: {
            backgroundColor: '#FFFFFF',
            padding: '32px',
            borderRadius: '20px',
            border: '1px solid #E5E5E5',
        },
        infoTitle: {
            fontSize: '20px',
            fontWeight: '700',
            color: '#2B2B2B',
            marginBottom: '12px',
        },
        infoText: {
            fontSize: '16px',
            color: '#414141',
            lineHeight: '1.7',
        },
        infoLink: {
            color: '#3457D5',
            textDecoration: 'none',
            fontWeight: '600',
        },
        form: {
            backgroundColor: '#FFFFFF',
            padding: '48px',
            borderRadius: '24px',
            border: '1px solid #E5E5E5',
        },
        formGroup: {
            marginBottom: '24px',
        },
        label: {
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#2B2B2B',
            marginBottom: '8px',
        },
        input: {
            width: '100%',
            padding: '14px 16px',
            fontSize: '16px',
            border: '1px solid #E5E5E5',
            borderRadius: '12px',
            fontFamily: 'Mulish, sans-serif',
            transition: 'all 0.3s',
            backgroundColor: '#FFFFFF',
        },
        textarea: {
            width: '100%',
            padding: '14px 16px',
            fontSize: '16px',
            border: '1px solid #E5E5E5',
            borderRadius: '12px',
            fontFamily: 'Mulish, sans-serif',
            minHeight: '150px',
            resize: 'vertical',
            transition: 'all 0.3s',
            backgroundColor: '#FFFFFF',
        },
        button: {
            width: '100%',
            padding: '16px',
            backgroundColor: '#D81F26',
            color: '#FFFFFF',
            fontSize: '16px',
            fontWeight: '600',
            border: 'none',
            borderRadius: '999px',
            cursor: 'pointer',
            transition: 'all 0.3s',
        },
        status: {
            marginTop: '16px',
            padding: '16px',
            backgroundColor: '#E4ECFF',
            color: '#3457D5',
            borderRadius: '12px',
            textAlign: 'center',
            fontWeight: '600',
        },
        select: {
            width: '100%',
            padding: '14px 16px',
            fontSize: '16px',
            border: '1px solid #E5E5E5',
            borderRadius: '12px',
            fontFamily: 'Mulish, sans-serif',
            backgroundColor: '#FFFFFF',
        },
        inlineGroup: {
            display: 'flex',
            gap: '16px',
            flexWrap: 'wrap',
        },
        inlineItem: {
            flex: '1 1 0',
            minWidth: '140px',
        },
    };

    const isMobile = window.innerWidth < 768;
    if (isMobile) {
        styles.section.padding = '60px 20px';
        styles.h2.fontSize = '32px';
        styles.grid.gridTemplateColumns = '1fr';
        styles.form.padding = '32px 24px';
    }

    return (
        <section style={styles.section} id="contact">
            <div style={styles.container}>
                <div style={styles.header}>
                    <h2 style={styles.h2}>
                        {t('contact.title')}<span style={styles.highlight}>{t('contact.titleHighlight')}</span>
                    </h2>
                    <p style={styles.subtext}>
                        {t('contact.subtext')}
                    </p>
                </div>

                <div style={styles.grid}>
                    <div style={styles.infoSection}>
                        <div style={styles.infoCard}>
                            <h3 style={styles.infoTitle}>📧 {t('contact.info.email')}</h3>
                            <p style={styles.infoText}>
                                <a
                                    href="mailto:support-corpusio@kirusa.com"
                                    style={styles.infoLink}
                                    onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                    onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                >
                                    support-corpusio@kirusa.com
                                </a>
                            </p>
                        </div>

                        <div style={styles.infoCard}>
                            <h3 style={styles.infoTitle}>📞 {t('contact.info.phone')}</h3>
                            <p style={styles.infoText}>
                                <a
                                    href="tel:+919652148469"
                                    style={styles.infoLink}
                                    onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                    onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                >
                                    +91 96521 48469
                                </a>
                            </p>
                        </div>

                        <div style={styles.infoCard}>
                            <h3 style={styles.infoTitle}>⏰ {t('contact.info.hours')}</h3>
                            <p style={styles.infoText}>
                                {(t('contact.info.hoursText') || []).map((line, i) => (
                                    <React.Fragment key={i}>{line}<br/></React.Fragment>
                                ))}
                            </p>
                        </div>
                    </div>

                    <form style={styles.form} onSubmit={handleSubmit}>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>{t('contact.form.name')}</label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                style={styles.input}
                                onFocus={(e) => e.target.style.borderColor = '#3457D5'}
                                onBlur={(e) => e.target.style.borderColor = '#E5E5E5'}
                            />
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>{t('contact.form.email')}</label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                style={styles.input}
                                onFocus={(e) => e.target.style.borderColor = '#3457D5'}
                                onBlur={(e) => e.target.style.borderColor = '#E5E5E5'}
                            />
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>{t('contact.form.phone')}</label>
                            <div style={styles.inlineGroup}>
                                <div style={{ ...styles.inlineItem, flex: '0 0 160px' }}>
                                    <select
                                        name="phoneCountryCode"
                                        value={formData.phoneCountryCode}
                                        onChange={(e) => handlePhoneCountryCodeChange(e.target.value)}
                                        style={styles.select}
                                    >
                                        <option value="">{t('contact.form.code')}</option>
                                        {countryOptions.map((country) => (
                                            <option key={country.isoCode} value={country.isoCode}>
                                                +{country.phonecode || '??'} ({country.isoCode})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div style={styles.inlineItem}>
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        inputMode="numeric"
                                        placeholder="Phone number"
                                        style={styles.input}
                                        onFocus={(e) => e.target.style.borderColor = '#3457D5'}
                                        onBlur={(e) => e.target.style.borderColor = '#E5E5E5'}
                                    />
                                </div>
                            </div>
                            <div style={{ marginTop: '6px', fontSize: '12px', color: '#6E6E6E' }}>
                                {getPhoneRequirementMessage(formData.phoneCountryCode || 'IN')}
                            </div>
                        </div>

                        <div style={styles.inlineGroup}>
                            <div style={{ ...styles.inlineItem, flex: '1 1 33%' }}>
                                <label style={styles.label}>{t('contact.form.country')}</label>
                                <select
                                    value={formData.country}
                                    onChange={(e) => handleCountrySelect(e.target.value)}
                                    style={styles.select}
                                >
                                    <option value="">{t('contact.form.selectCountry')}</option>
                                    {countryOptions.map((country) => (
                                        <option key={country.isoCode} value={country.isoCode}>
                                            {country.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div style={{ ...styles.inlineItem, flex: '1 1 33%' }}>
                                <label style={styles.label}>{t('contact.form.state')}</label>
                                <select
                                    value={formData.state}
                                    onChange={(e) => handleStateSelect(e.target.value, formData.country)}
                                    style={styles.select}
                                    disabled={!formData.country}
                                >
                                    <option value="">{t('contact.form.selectState')}</option>
                                    {stateOptions.map((state) => (
                                        <option key={state.isoCode} value={state.isoCode}>
                                            {state.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div style={{ ...styles.inlineItem, flex: '1 1 33%' }}>
                                <label style={styles.label}>{t('contact.form.city')}</label>
                                <select
                                    value={formData.city}
                                    onChange={(e) => handleCitySelect(e.target.value)}
                                    style={styles.select}
                                    disabled={!formData.state}
                                >
                                    <option value="">{t('contact.form.selectCity')}</option>
                                    {cityOptions.map((city) => (
                                        <option key={city.name} value={city.name}>
                                            {city.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>{t('contact.form.company')}</label>
                            <input
                                type="text"
                                name="company"
                                value={formData.company}
                                onChange={handleChange}
                                style={styles.input}
                                onFocus={(e) => e.target.style.borderColor = '#3457D5'}
                                onBlur={(e) => e.target.style.borderColor = '#E5E5E5'}
                            />
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>{t('contact.form.message')}</label>
                            <textarea
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                required
                                style={styles.textarea}
                                onFocus={(e) => e.target.style.borderColor = '#3457D5'}
                                onBlur={(e) => e.target.style.borderColor = '#E5E5E5'}
                            />
                        </div>

                        <button
                            type="submit"
                            style={styles.button}
                            disabled={status === 'sending'}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#B01820'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#D81F26'}
                        >
                            {status === 'sending' ? t('contact.form.sending') : t('contact.form.send')}
                        </button>

                        {status === 'success' && (
                            <div style={{ ...styles.status, backgroundColor: '#E8F1E6', color: '#7CD845' }}>
                                {t('contact.form.success')}
                            </div>
                        )}
                        {status === 'error' && (
                            <div style={{ ...styles.status, backgroundColor: '#FFE0E0', color: '#D81F26' }}>
                                {t('contact.form.error')}
                            </div>
                        )}
                        {status && status !== 'success' && status !== 'error' && status !== 'sending' && (
                            <div style={{ ...styles.status, backgroundColor: '#FFE3CC', color: '#F28C46' }}>
                                {status}
                            </div>
                        )}
                    </form>
                </div>
            </div>
        </section>
    );
};

export default Contact;
